import React, { useEffect, useState } from 'react'
import { Col, Form, Input, Modal, Pagination, Radio, Row } from 'antd'
import { useParams } from 'react-router-dom'
import Spinner from 'components/Spinner'
import ButtonSpinner from 'components/global/ButtonSpinner'
import Cancel from 'assets/Images/close.svg'
import Recepient from 'pages/Family/Profiles/NotesComp/Components/Recepient'
import ContentArea from 'pages/Family/Profiles/NotesComp/Components/ContentArea'
import TaggedPeople from 'pages/Family/Profiles/NotesComp/Components/TaggedPeople'
import {
  useAddLeagueNote,
  useDeleteLeagueNote,
  useGetLeagueNotes,
} from 'api/league/LeagueNotes/leagueNotes'
import 'pages/Family/Profiles/NotesComp/styles/style.scss'

/* ── Add Note Modal ───────────────────────────────────── */
interface AddNoteModalProps {
  open: boolean
  onClose: () => void
  leagueEventId: string
}

const AddNoteModal: React.FC<AddNoteModalProps> = ({
  open,
  onClose,
  leagueEventId,
}) => {
  const [form] = Form.useForm()
  const [basicField, setBasicField] = useState({ type: 'comment', method: 'none' })
  const addNote = useAddLeagueNote()

  const adminId =
    typeof window !== 'undefined' ? localStorage.getItem('adminId') || '' : ''

  const handleCancel = () => {
    form.resetFields()
    setBasicField({ type: 'comment', method: 'none' })
    onClose()
  }

  const handleSubmit = (values: any) => {
    addNote.mutate(
      {
        adminId,
        payload: {
          type: values.noteType,
          method: values.method,
          description: values.description,
          leagueId: leagueEventId,
        },
      },
      {
        onSuccess: () => {
          form.resetFields()
          setBasicField({ type: 'comment', method: 'none' })
          onClose()
        },
      },
    )
  }

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      centered
      footer={null}
      className="add-note-modal"
    >
      <div className="scroller note-modal-height">
        <div className="flex-center justify-between">
          <h2 className="w-600 text-24">Add a note</h2>
          <img
            src={Cancel}
            alt="close"
            style={{ cursor: 'pointer' }}
            onClick={handleCancel}
          />
        </div>

        <Form
          form={form}
          onFinish={handleSubmit}
          initialValues={{ noteType: 'comment', method: 'none' }}
          className="mt-20px"
        >
          <Row>
            <Col span={24}>
              <label className="custom-field-label">Note Type</label>
              <Form.Item
                name="noteType"
                rules={[{ required: true, message: 'Note type is required' }]}
              >
                <Radio.Group
                  style={{ width: '100%' }}
                  defaultValue="comment"
                  onChange={e =>
                    setBasicField({ ...basicField, type: e.target.value })
                  }
                >
                  <Radio value="comment">Comment</Radio>
                  <Radio value="tips">Tips</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col span={24}>
              <label className="custom-field-label">Method</label>
              <Form.Item
                name="method"
                rules={[{ required: true, message: 'Method is required' }]}
              >
                <Radio.Group style={{ width: '100%' }} defaultValue="none">
                  <Radio value="none">None</Radio>
                  <Radio value="text">Text</Radio>
                  <Radio value="email">Email</Radio>
                  <Radio value="phone-call">Phone Call</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col span={24}>
              <label className="custom-field-label">Note</label>
              <Form.Item
                name="description"
                rules={[{ required: true, message: 'Please enter note' }]}
              >
                <Input.TextArea
                  className="note-field"
                  placeholder="Enter a note here"
                  autoSize={{ minRows: 3, maxRows: 7 }}
                />
              </Form.Item>
            </Col>
          </Row>

          <div className="flex align-center justify-end gap-30px mt-30px pb-30px">
            <button className="prim-btn" type="submit" disabled={addNote.isLoading}>
              {addNote.isLoading ? <ButtonSpinner size={20} color="#000" /> : 'Create'}
            </button>
          </div>
        </Form>
      </div>
    </Modal>
  )
}

/* ── Notes Tab ────────────────────────────────────────── */
const NotesTab: React.FC = () => {
  const { id: leagueEventId } = useParams<{ id: string }>()
  const [page, setPage] = useState(1)
  const limit = 10
  const [selectedNote, setSelectedNote] = useState<any>(undefined)
  const [addModalOpen, setAddModalOpen] = useState(false)

  const { data, isLoading, isFetching } = useGetLeagueNotes(
    leagueEventId,
    page,
    limit,
  )
  const deleteNote = useDeleteLeagueNote(leagueEventId)

  useEffect(() => {
    if (data?.records?.length > 0) {
      setSelectedNote(data.records[0])
    } else {
      setSelectedNote(undefined)
    }
  }, [data])

  const handleDelete = (noteId: string) => {
    deleteNote.mutate(noteId)
  }

  return (
    <div className="bg-grey padding-top-10" style={{ minHeight: '60vh' }}>
      <div className="bg-white" style={{ minHeight: '80vh' }}>
        {isLoading || isFetching ? (
          <div className="flex justify-center align-center" style={{ padding: 40 }}>
            <Spinner />
          </div>
        ) : (
          <div>
            {/* Top bar */}
            <div className="border-btm p-20px flex justify-between align-center">
              <h3 className="text-16 w-600">
                {data?.count ?? 0} Note{data?.count !== 1 ? 's' : ''}
              </h3>
              <button
                className="borderd-btn"
                onClick={() => setAddModalOpen(true)}
              >
                + Add note
              </button>
            </div>

            {/* 3-column layout */}
            <div className="grid-notes h-full">
              <div className="border-right">
                <Recepient
                  recepData={data?.records ?? []}
                  setNote={setSelectedNote}
                />
              </div>
              <div className="notes-bg">
                <ContentArea NoteData={selectedNote} />
              </div>
              <div className="border-left">
                <TaggedPeople TaggedData={selectedNote} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Pagination */}
      {(data?.count ?? 0) > limit && (
        <Row className="justify-center flex mt-40px">
          <Pagination
            className="pb-20"
            current={page}
            total={data?.count ?? 0}
            pageSize={limit}
            onChange={setPage}
            style={{ width: '100%', display: 'flex' }}
          />
        </Row>
      )}

      <AddNoteModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        leagueEventId={leagueEventId}
      />
    </div>
  )
}

export default NotesTab
